import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import { buildWhopUserData } from "../AdvancedMatching";
import type { MatchIdentity } from "../AdvancedMatching";

export { WhopPixelAdapter, WHOP_ADAPTER_KEY, WHOP_SCRIPT, WHOP_PIXEL_CONFIG };

const WHOP_ADAPTER_KEY = "whop";
const WHOP_ORIGIN = "https://t.whop.tw";
const WHOP_SCRIPT = WHOP_ORIGIN + "/s.js";
// `page` es la columna vertebral de la atribución de Whop: toda página tiene
// que mandarlo (aquí llega del mapping page_view del manifest). Nunca con PII.
const WHOP_PAGE_EVENT = "page";
const WHOP_IDENTIFY_EVENT = "identify";

/**
 * Config que el bootstrap de s.js lee de `whop.c` (`{...window.__whopApp,
 * ...whop.c}`) ANTES de drenar la cola. Decisión de producto: sin
 * auto-captura de PII — `forms` leería cualquier campo de cualquier
 * formulario al cambiar y `urlIdentity` el email/teléfono/nombre de la
 * query. La identidad la manda el SDK, explícita, en el optin. El resto de
 * opciones (links, iframes, sc, autoPage) se quedan con su default.
 */
const WHOP_PIXEL_CONFIG: Readonly<Record<string, boolean>> = {
  forms: false,
  urlIdentity: false,
};

// Lo que el SDK usa del objeto global de Whop (stub o SDK ya cargado)
interface WhopScopedTracker {
  track: (...args: unknown[]) => void;
}

interface WhopLike {
  setScope: (...ids: string[]) => void;
  scope: (...ids: string[]) => WhopScopedTracker;
}

// El stub del snippet oficial: cola `q` de arrays [timestamp, ...args] que
// s.js drena al cargar, `s` = scope por defecto vigente, `o` = origen.
interface WhopStub extends WhopLike {
  q: unknown[][];
  t: number;
  s: string[];
  o: string;
  c: Record<string, unknown>;
  track: (...args: unknown[]) => void;
}

/**
 * Adaptador del píxel de Whop Ads (`whop`).
 *
 * Particularidades de la red:
 *
 * - **Instancia compartida**: si `window.whop` ya existe (el tenant instaló
 *   el snippet de Whop, p.ej. por GTM) NO se redefine ni se inyecta s.js otra
 *   vez, igual que hace el snippet oficial; tampoco se toca su config.
 * - **`setScope` REEMPLAZA el scope por defecto** (al que van los page/leave
 *   automáticos): se llama siempre con el conjunto COMPLETO de cuentas
 *   conocidas — las nuestras más las que ya tuviera un snippet del tenant.
 * - **`scope(id).track` siempre**: cada evento explícito va solo a su cuenta,
 *   como `trackSingle` en Meta o `measureSingle` en OpenAI.
 * - **`page` no es automático aquí**: lo dispara el mapping `page_view` del
 *   manifest, como cualquier otro evento.
 * - **Custom events**: cualquier nombre que no sea estándar lo es; la llamada
 *   no cambia, así que `event.custom` no se usa.
 * - **`event_id`** en las props es el gancho de la dedup con la copia
 *   servidor (Whop se queda una por event_name + event_id).
 * - **Identidad EN CLARO** (`identify` y `email`/`phone` en las conversiones):
 *   Whop descarta lo hasheado. El `identify` es síncrono, así que a
 *   diferencia de Meta y OpenAI el Lead de la propia página del optin ya sale
 *   con la identidad (PixelManager llama a init antes que a track).
 * - **Sin API de consentimiento**: su `setConsent` es un no-op documentado.
 */
class WhopPixelAdapter implements BrowserPixelAdapter {
  readonly key = WHOP_ADAPTER_KEY;
  private _loaded = false;
  // Cuentas de Whop (`biz_...`) inicializadas por este adaptador, en orden
  private _pixelIds: string[] = [];
  // Scope que ya tenía un `window.whop` del tenant al cargar
  private _foreignScope: string[] = [];
  // Huella de la última identidad enviada por píxel: un identify por identidad
  private _identityApplied: Record<string, string> = {};
  // email/phone conocidos por píxel, para las conversiones posteriores
  private _matchKeys: Record<string, Record<string, string>> = {};
  private _onScriptError: ScriptErrorReporter | null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      const existing = window.whop;
      if (existing && !isDomNamedAccess(existing)) {
        // Instancia del tenant. Sus ids de scope se conservan: sin ellos, el
        // setScope con los nuestros dejaría sus page/leave automáticos sin
        // destino.
        this._foreignScope = readScopeIds(existing);
        return;
      }

      // Un elemento con id="whop" también "existe" en window (acceso por
      // nombre del DOM); el snippet oficial se rendiría ahí y el píxel no
      // cargaría nunca, en silencio. La propiedad propia lo tapa sin tocar
      // el elemento.
      window.whop = createWhopStub();
      injectVendorScript(WHOP_SCRIPT, this.key, this._onScriptError);
    } catch {
      // sin píxel de Whop la página sigue funcionando igual
    }
  }

  init(pixelId: string, identity: MatchIdentity | null): void {
    try {
      if (!pixelId) {
        return;
      }
      const whop = this._whop();
      if (!whop) {
        return;
      }

      if (this._pixelIds.indexOf(pixelId) === -1) {
        this._pixelIds.push(pixelId);
        whop.setScope(...this._scopeIds());
      }

      const user = buildWhopUserData(identity);
      if (Object.keys(user).length === 0) {
        return;
      }
      const fingerprint = JSON.stringify([
        user.email ?? "",
        user.phone ?? "",
        user.first_name ?? "",
        user.last_name ?? "",
      ]);
      if (this._identityApplied[pixelId] === fingerprint) {
        return;
      }
      this._identityApplied[pixelId] = fingerprint;

      const matchKeys: Record<string, string> = {};
      if (user.email) matchKeys.email = user.email;
      if (user.phone) matchKeys.phone = user.phone;
      this._matchKeys[pixelId] = matchKeys;

      whop.scope(pixelId).track(WHOP_IDENTIFY_EVENT, user);
    } catch {
      // un init fallido no puede tumbar el resto de píxeles
    }
  }

  track(pixelId: string, event: BrowserEventPayload, eventId: string): void {
    try {
      const whop = this._whop();
      if (!whop || !pixelId || !event?.name) {
        return;
      }
      const props: Record<string, unknown> = {
        ...(event.params ?? {}),
        event_id: eventId,
        ...(event.name === WHOP_PAGE_EVENT
          ? {}
          : (this._matchKeys[pixelId] ?? {})),
      };
      whop.scope(pixelId).track(event.name, props);
    } catch {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  // Whop no tiene API de consentimiento: su setConsent es un no-op
  // documentado. Sin CMP, el estado es el implícito de todo el SDK.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setConsent(_granted: boolean): void {
    // no-op a propósito
  }

  private _scopeIds(): string[] {
    const ids = this._foreignScope.slice();
    for (const id of this._pixelIds) {
      if (ids.indexOf(id) === -1) {
        ids.push(id);
      }
    }
    return ids;
  }

  private _whop(): WhopLike | null {
    const whop = window.whop;
    return whop &&
      typeof whop.setScope === "function" &&
      typeof whop.scope === "function"
      ? whop
      : null;
  }
}

// Réplica del snippet oficial de Whop (misma forma de cola, que es lo que
// s.js drena) + la config sin auto-captura de PII.
function createWhopStub(): WhopStub {
  const stub: WhopStub = {
    q: [],
    t: Date.now(),
    s: [],
    o: WHOP_ORIGIN,
    c: { ...WHOP_PIXEL_CONFIG },
    track: (...args: unknown[]) => {
      stub.q.push([Date.now(), ...args]);
    },
    setScope: (...ids: unknown[]) => {
      stub.s = ids.filter((id): id is string => typeof id === "string");
      stub.q.push([Date.now(), "setScope", ...stub.s]);
    },
    scope: (...ids: unknown[]) => ({
      track: (...args: unknown[]) => {
        stub.q.push([Date.now(), ...args, { __scope: ids }]);
      },
    }),
  };
  return stub;
}

function readScopeIds(whop: unknown): string[] {
  try {
    const scope = (whop as { s?: unknown }).s;
    return Array.isArray(scope)
      ? scope.filter((id): id is string => typeof id === "string" && id !== "")
      : [];
  } catch {
    return [];
  }
}

function isDomNamedAccess(value: unknown): boolean {
  try {
    return (
      (typeof Element !== "undefined" && value instanceof Element) ||
      (typeof HTMLCollection !== "undefined" && value instanceof HTMLCollection)
    );
  } catch {
    return false;
  }
}
